import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../constants/validation'
import { useAsyncCallback, useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { getIdFilterParam } from '../../../../utils/filter'
import { getEntityProfileHeaderInfoItemInitialValues } from '../../../Entity'
import {
  DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE,
  DOC_FLOW_FORM_FIELD_VARIABLE,
} from '../../DocFlow.constants'
import {
  DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT,
  getDocFlowAddRecipientFieldsEditInitialValues,
} from '../../DocFlowAddRecipientFields'
import {
  deleteCombining,
  DOC_FLOW_COMBINING_ACTION_DELETE,
  DOC_FLOW_COMBINING_ACTION_EDIT_FINAL_DOCUMENT_TITLE,
  DOC_FLOW_COMBINING_ACTION_EDIT_RECIPIENT,
  DOC_FLOW_COMBINING_ACTION_EDIT_SEQUENCE,
  DOC_FLOW_COMBINING_FORM_FIELD_FINAL_FILE_NAME,
  DOC_FLOW_COMBINING_MASS_ACTION_COMBINED,
  DOC_FLOW_COMBINING_MASS_ACTION_DELETE,
  DOC_FLOW_COMBINING_MASS_ACTION_EDIT_FINAL_DOCUMENT_TITLE,
  DOC_FLOW_COMBINING_MASS_ACTION_EDIT_FINAL_DOCUMENT_TITLE_FIELD,
  DOC_FLOW_COMBINING_MASS_ACTION_EDIT_SEQUENCE_DOCUMENT,
  DOC_FLOW_COMBINING_MASS_ACTION_UNCOMBINED,
  DocFlowCombiningFieldName,
  getCombiningTableNotificationTitle,
  getEditFinalFileTitleMassPayload,
  getEditFinalFileTitlePayload,
  performDocFlowCombiningMassAction,
  updateCombining,
} from '../../DocFlowCombining'
import { DocFlowEditFinalDocumentModal } from '../../DocFlowEditFinalDocumentModal'
import { DocFlowEditSequenceModal } from '../../DocFlowEditSequenceModal'
import {
  DocFlowRecipientModal,
  getValidationSchemaDocFlowRecipientModalSingleField,
} from '../../DocFlowRecipientModal'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowProhibitDeleteLastTableItem } from '../useDocFlowProhibitDeleteLastTableItem'
import { useGetUploadingFiles } from '../useGetUploadingFiles'

import {
  isChangedCombinedValue,
  isEditSequenceMassAction,
  isUpdateProfileAfterMassDelete,
} from './useCombiningTableActions.utils'

export function useCombiningTableActions({
  filterValues,
  getAsyncTaskId,
  pagination,
  selectionData,
  tableItems,
  onAddFinishMassActionNotification,
  onAsyncAction,
  onReloadTable,
}) {
  const {
    docFlow,
    documentId,
    handleCloseModal,
    handleReloadDocFlow,
    setModal,
  } = useDocFlowContext()

  const { excludeIds, handleCleanSelection, selectAllPages, selectedIds } =
    selectionData

  const { t } = useTranslation()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { uploadingFiles } = useGetUploadingFiles()

  const massAction = useAsyncCallback(
    ({ action, ids, value = EMPTY_OBJECT }) => {
      const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })
      const excludeId = getMassActionExcludeIdParam({
        excludeIds,
        selectAllPages,
      })

      const params = {
        action,
        all: selectAllPages,
        filters: {
          ...filterValues,
          exclude_id: excludeId,
          id: getIdFilterParam({
            filterValues,
            ids: id,
            selectAllPages,
          }),
        },
        payload: value,
      }

      const countOfItems = selectAllPages ? pagination.count : id.length

      handleCloseModal()

      setPerformingMassAction(true)

      return performDocFlowCombiningMassAction(documentId, params)
        .then(({ data }) => {
          handleCleanSelection()

          const asyncTask = getAsyncTaskId(data)

          if (asyncTask) {
            onAsyncAction({
              action,
              task: asyncTask,
              totalCount: countOfItems,
            })

            return data
          }

          onReloadTable()
            .then(([response]) => {
              if (
                isUpdateProfileAfterMassDelete({
                  action,
                  deleteItems: id,
                  response,
                  tableItems,
                })
              ) {
                handleReloadDocFlow()
              }
            })
            .finally(() => {
              onAddFinishMassActionNotification(data, {
                action,
                totalCount: countOfItems,
              })
            })

          return data
        })
        .catch(() => {
          handleAddErrorNotification(
            'Notification.DocumentStaplingCombiningMassActionErrorTitleKey'
          )
        })
        .finally(() => setPerformingMassAction(false))
    },
    [
      documentId,
      excludeIds,
      filterValues,
      getAsyncTaskId,
      handleAddErrorNotification,
      handleCleanSelection,
      handleCloseModal,
      handleReloadDocFlow,
      onAddFinishMassActionNotification,
      onAsyncAction,
      onReloadTable,
      pagination.count,
      selectAllPages,
      selectedIds,
      tableItems,
    ]
  )

  function handleSuccessAction(action) {
    handleCloseModal()
    handleCleanSelection()

    onReloadTable().finally(() =>
      handleAddSuccessNotification({
        title: getCombiningTableNotificationTitle(action),
      })
    )
  }

  function handleErrorAction(title) {
    handleCloseModal()
    handleAddErrorNotification(title)
  }

  const handleUpdateCombining = useAsyncCallback(
    ({ action, item, params }) =>
      updateCombining({ combiningId: item.id, documentId, ...params })
        .then(({ data }) => {
          const asyncTask = getAsyncTaskId(data)

          if (asyncTask) {
            onAsyncAction({
              action,
              task: asyncTask,
            })

            handleCloseModal()

            return data
          }

          handleSuccessAction(action)

          return data
        })
        .catch(() => {
          handleErrorAction(
            t('Notification.DocumentStaplingCombiningActionErrorKey')
          )
        }),
    [
      documentId,
      getAsyncTaskId,
      handleCloseModal,
      handleErrorAction,
      handleSuccessAction,
      onAsyncAction,
      t,
    ]
  )

  function getHandleAddRecipientToItem({ action, item }) {
    return ({ value }) =>
      handleUpdateCombining({
        action,
        item,
        params: {
          [DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT]:
            value[DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT].value,
        },
      }).then(() => {
        // we need update profile because of updating
        // permission for next phase button after assigning recipient
        if (!item.recipient) {
          handleReloadDocFlow()
        }
      })
  }

  function getHandleEditItemFinalFileName({ action, item }) {
    return ({ value }) => {
      handleUpdateCombining({
        action,
        item,
        params: {
          [DOC_FLOW_COMBINING_FORM_FIELD_FINAL_FILE_NAME]:
            value[DOC_FLOW_COMBINING_FORM_FIELD_FINAL_FILE_NAME],
        },
      })
    }
  }

  const handleEditItemSequence = useAsyncCallback(
    ({ item, updatedList }) => {
      if (item) {
        return handleUpdateCombining({
          action: DOC_FLOW_COMBINING_ACTION_EDIT_SEQUENCE,
          item,
          params: {
            [DocFlowCombiningFieldName.Uploading]: updatedList,
          },
        })
      } else {
        return massAction({
          action: DOC_FLOW_COMBINING_MASS_ACTION_EDIT_SEQUENCE_DOCUMENT,
          value: {
            [DocFlowCombiningFieldName.Uploading]: updatedList,
          },
        })
      }
    },
    [handleUpdateCombining, massAction]
  )

  function getMassActionHandler({ action }) {
    return ({ value } = EMPTY_OBJECT) => {
      massAction({ action, value })
    }
  }

  function handleClickAddRecipient({ action, item }) {
    return setModal(
      <DocFlowRecipientModal
        edit
        documentId={documentId}
        initialValues={getDocFlowAddRecipientFieldsEditInitialValues(item)}
        title='DocumentStapling.ModalEditRecipientTitleKey'
        validationSchema={getValidationSchemaDocFlowRecipientModalSingleField}
        onCloseModal={handleCloseModal}
        onSubmit={getHandleAddRecipientToItem({ action, item })}
      />
    )
  }

  function handleClickEditFinalDocumentTitle({ action, item }) {
    return setModal(
      <DocFlowEditFinalDocumentModal
        fieldName={DOC_FLOW_COMBINING_FORM_FIELD_FINAL_FILE_NAME}
        getPayload={getEditFinalFileTitlePayload}
        initialValues={{
          ...getEntityProfileHeaderInfoItemInitialValues({
            name: DOC_FLOW_COMBINING_FORM_FIELD_FINAL_FILE_NAME,
            value: item[DocFlowCombiningFieldName.FinalFile]?.name,
          }),
          ...getEntityProfileHeaderInfoItemInitialValues({
            name: DOC_FLOW_FORM_FIELD_VARIABLE,
            value: FORMIK_EMPTY_VALUE,
          }),
        }}
        onClose={handleCloseModal}
        onSubmit={getHandleEditItemFinalFileName({ action, item })}
      />
    )
  }

  function handleClickEditFinalDocumentTitleMass({ action }) {
    setModal(
      <DocFlowEditFinalDocumentModal
        fieldName={
          DOC_FLOW_COMBINING_MASS_ACTION_EDIT_FINAL_DOCUMENT_TITLE_FIELD
        }
        getPayload={getEditFinalFileTitleMassPayload}
        initialValues={{
          ...getEntityProfileHeaderInfoItemInitialValues({
            name: DOC_FLOW_COMBINING_MASS_ACTION_EDIT_FINAL_DOCUMENT_TITLE_FIELD,
            value: docFlow[DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE],
          }),
          ...getEntityProfileHeaderInfoItemInitialValues({
            name: DOC_FLOW_FORM_FIELD_VARIABLE,
            value: FORMIK_EMPTY_VALUE,
          }),
        }}
        onClose={handleCloseModal}
        onSubmit={getMassActionHandler({ action })}
      />
    )
  }

  function handleClickEditSequence({ action, item }) {
    return setModal(
      <DocFlowEditSequenceModal
        files={
          isEditSequenceMassAction(action)
            ? uploadingFiles
            : item[DocFlowCombiningFieldName.Uploading]
        }
        item={item}
        onClose={handleCloseModal}
        onSubmit={handleEditItemSequence}
      />
    )
  }

  function getDeleteItemHandler({ action, item }) {
    return () => {
      deleteCombining({ combiningId: item.id, documentId })
        .then(() => {
          handleSuccessAction(action)

          if (!tableItems || tableItems?.items.length - 1 <= 1) {
            handleReloadDocFlow()
          }
        })
        .catch(() => {
          handleErrorAction(
            'Notification.DocumentStaplingCombiningActionErrorKey'
          )
        })
    }
  }

  const { handleDelete } = useDocFlowProhibitDeleteLastTableItem({
    excludeIds,
    getDeleteItemHandler,
    getMassActionHandler,
    massActionName: DOC_FLOW_COMBINING_MASS_ACTION_DELETE,
    selectAllPages,
    selectedIds,
    tableItems,
  })

  function handleCombinedDocument(item, value) {
    if (isChangedCombinedValue({ item, value })) {
      handleUpdateCombining({
        item,
        params: {
          [DocFlowCombiningFieldName.Combined]: value,
        },
      })
    }
  }

  function setItemAction(action, item) {
    switch (action) {
      case DOC_FLOW_COMBINING_ACTION_EDIT_RECIPIENT:
        handleClickAddRecipient({ action, item })
        break
      case DOC_FLOW_COMBINING_ACTION_EDIT_FINAL_DOCUMENT_TITLE:
        handleClickEditFinalDocumentTitle({ action, item })
        break
      case DOC_FLOW_COMBINING_ACTION_EDIT_SEQUENCE:
        handleClickEditSequence({ action, item })
        break
      case DOC_FLOW_COMBINING_ACTION_DELETE:
        handleDelete({ action, item })
        break
      default:
        break
    }
  }

  function setMassAction(action) {
    switch (action) {
      case DOC_FLOW_COMBINING_MASS_ACTION_COMBINED:
        return massAction({ action })

      case DOC_FLOW_COMBINING_MASS_ACTION_UNCOMBINED:
        return massAction({ action })

      case DOC_FLOW_COMBINING_MASS_ACTION_EDIT_FINAL_DOCUMENT_TITLE:
        return handleClickEditFinalDocumentTitleMass({ action })

      case DOC_FLOW_COMBINING_MASS_ACTION_EDIT_SEQUENCE_DOCUMENT:
        return handleClickEditSequence({ action })

      case DOC_FLOW_COMBINING_MASS_ACTION_DELETE:
        return handleDelete({ action })

      default:
        return null
    }
  }

  return {
    handleCombinedDocument,
    handleUpdateCombining,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  }
}
